import { useEffect } from "react";
import { Route, Switch, useLocation } from "wouter";
import { PublicLayout } from "./components/layout";
import { HomePage } from "./pages/home";
import { AboutPage } from "./pages/about";
import { ProjectsPage } from "./pages/projects";
import { ProjectDetailPage } from "./pages/project-detail";
import { UnitDetailPage } from "./pages/unit-detail";
import { UnitSpecsPage } from "./pages/unit-specs";
import { UnitComparePage } from "./pages/unit-compare";
import { ContactPage } from "./pages/contact";
import { StaticPagePage } from "./pages/static-page";
import { NotFoundPage } from "./pages/not-found";
import { AdminLogin } from "./pages/admin/login";
import { AdminLayout } from "./pages/admin/layout";
import { AdminProjectsList } from "./pages/admin/projects-list";
import { AdminProjectEdit } from "./pages/admin/project-edit";
import { AdminFormBuilder } from "./pages/admin/form-builder";
import { AdminMessages } from "./pages/admin/messages";
import { AdminChatLog } from "./pages/admin/chat-log";
import { AdminDashboard } from "./pages/admin/dashboard";
import { AdminSiteSettings } from "./pages/admin/site-settings";
import { AdminTeam } from "./pages/admin/team";
import { AdminServicesPage } from "./pages/admin/services";
import { AdminPagesList } from "./pages/admin/pages-list";
import { AdminPageEdit } from "./pages/admin/page-edit";
import { ComingSoonPage } from "./pages/coming-soon";
import { MaintenanceAdminBar } from "./components/maintenance-bar";
import { useSiteHidden } from "./lib/maintenance";

/**
 * SPA-navigációnál a böngésző megtartja a görgetési pozíciót, így egy lakásra
 * kattintva az új oldal "középen" nyílt meg (F-MANUAL-IIH49K48,
 * F-MANUAL-2VSNMEPM). Útvonal-váltáskor az oldal tetejére ugrunk; az oldalon
 * belüli horgony-görgetéseket (azonos útvonal) nem érinti.
 */
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

export function App() {
  return (
    <>
      <ScrollToTop />
      <Switch>
      {/* Admin (no public layout, no header/footer) */}
      <Route path="/admin/login">
        <AdminLogin />
      </Route>
      <Route path="/admin" nest>
        <AdminLayout>
          <Switch>
            <Route path="/" component={AdminDashboard} />
            <Route path="/projects" component={AdminProjectsList} />
            <Route path="/projects/new">
              {() => <AdminProjectEdit projectId={null} />}
            </Route>
            <Route path="/projects/:id">
              {(params) => <AdminProjectEdit projectId={params.id} />}
            </Route>
            <Route path="/form-builder" component={AdminFormBuilder} />
            <Route path="/messages" component={AdminMessages} />
            <Route path="/chat-log" component={AdminChatLog} />
            <Route path="/site-settings" component={AdminSiteSettings} />
            <Route path="/team" component={AdminTeam} />
            <Route path="/services" component={AdminServicesPage} />
            <Route path="/pages" component={AdminPagesList} />
            <Route path="/pages/new">
              {() => <AdminPageEdit pageId={null} />}
            </Route>
            <Route path="/pages/:id">
              {(params) => <AdminPageEdit pageId={params.id} />}
            </Route>
            <Route component={NotFoundPage} />
          </Switch>
        </AdminLayout>
      </Route>

      {/* Public site */}
      <Route>
        <PublicSite />
      </Route>
      </Switch>
    </>
  );
}

/**
 * A publikus oldal — vagy helyette a „Hamarosan” oldal, amíg a mód aktív
 * (admin → Oldal beállítások → Oldal állapota). A bejelentkezett admin a
 * valódi tartalmat kapja, egy figyelmeztető sávval a tetején.
 */
function PublicSite() {
  const hidden = useSiteHidden();
  if (hidden) return <ComingSoonPage />;
  return (
    <>
      <MaintenanceAdminBar />
      <PublicLayout>
          <Switch>
            <Route path="/" component={HomePage} />
            <Route path="/rolunk" component={AboutPage} />
            <Route path="/projektek" component={ProjectsPage} />
            <Route path="/projektek/:slug/muszaki-adatok">
              {(params) => <UnitSpecsPage slug={params.slug} />}
            </Route>
            <Route path="/projektek/:slug/osszehasonlitas">
              {(params) => <UnitComparePage slug={params.slug} />}
            </Route>
            <Route path="/projektek/:slug/:unitSlug">
              {(params) => (
                <UnitDetailPage slug={params.slug} unitSlug={params.unitSlug} />
              )}
            </Route>
            <Route path="/projektek/:slug">
              {(params) => <ProjectDetailPage slug={params.slug} />}
            </Route>
            <Route path="/kapcsolat" component={ContactPage} />
            <Route path="/o/:slug">
              {(params) => <StaticPagePage slug={params.slug} />}
            </Route>
            <Route component={NotFoundPage} />
          </Switch>
      </PublicLayout>
    </>
  );
}
